
import { google } from 'google-maps';
import { Component, Inject, Watch, Mixins } from 'vue-property-decorator';
import { GoogleMapConfig } from '@/flightpath-components/types/GoogleMapConfig';
import GoogleMapLoader from '@/components/Map/GoogleMapLoader.vue';
import GoogleMapMachineMarker from '@/components/Map/GoogleMapMachineMarker.vue';
import GoogleMapPolyLine from '@/components/Map/GoogleMapPolyLine.vue';
import GoogleMapPolygon from '@/components/Map/GoogleMapPolygon.vue';
import GoogleMapGroundOverlay from '@/components/Map/GoogleMapGroundOverlay.vue';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
import { PubSub } from '@/PubSubClass';
import { awsConfiguration } from '@/aws-configuration';
import { MqttMessage } from '@/flightpath-components/models/mqttPayload/GPSUpdateMessage';
import { MachineMarker } from '@/flightpath-components/models/mqttPayload/MachineMarkerMessage';
import { MachineMarkerOptions } from '@/flightpath-components/models/MachineMarkerOptions';
import { Overlay } from '@/flightpath-components/models/Overlay';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import MachineStore from '@/store/modules/machine';
import { getModule } from 'vuex-module-decorators';
import GoogleMapRecordedPath from '@/components/Map/GoogleMapRecordedPath.vue';
import MqttMixIn from '@/mixins/mqttManagement';
import MenuStore from '@/store/modules/menu';
import { RenderHelper } from '@/services/RenderHelper';
import MissionStore from '@/store/modules/missions';
import { LayerList } from '@/flightpath-components/models/LayerList';
import { LayerType } from '@/flightpath-components/models/enums/LayerType';
import { GridLocation } from '@/flightpath-components/models/GridLocation';
import GlobalStore from '@/store/modules/global';
import { ConvertMachineRasterMessage } from '@/flightpath-components/models/mqttPayload/MachineRasterMessage';
import { RasterModel } from '@/flightpath-components/models/RasterModel';
import { DeployedSinglePathMission } from '@/flightpath-components/models/DeployedSinglePathMission';
import UpdateStore from '@/store/modules/update';
import { UpdateMachineUpdateIcon } from '@/models/Icons';
import moment from 'moment';

@Component({
  inject: ['container'],
  // Specify `components` option.
  // See Vue.js docs for all available options:
  // https://vuejs.org/v2/api/#Options-Data
  components: {
    GoogleMapLoader,
    GoogleMapMachineMarker,
    GoogleMapPolyLine,
    GoogleMapPolygon,
    GoogleMapGroundOverlay,
    GoogleMapRecordedPath
  }
})
export default class SingleMachineMap extends Mixins(MqttMixIn) {
  apiKey = awsConfiguration.apiKey;

  recordedPolyline: google.maps.PolylineOptions = {
    path: [],
    strokeColor: 'White',
    strokeOpacity: 0.5,
    visible: true
  };

  markers: Array<MachineMarkerOptions> = [];

  polyLines: Array<google.maps.PolylineOptions> = [];

  polygons: Array<google.maps.PolygonOptions> = [];

  overlays: Array<Overlay> = [];

  missionPlan: DeployedSinglePathMission | null = null;

  public machineStore = getModule(MachineStore, this.$store);
  public menuStore = getModule(MenuStore, this.$store);
  missionStore = getModule(MissionStore, this.$store);
  public globalStore = getModule(GlobalStore, this.$store);
  public updateStore = getModule(UpdateStore, this.$store);

  // @Inject()
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  // IoC container provided from App.ts is injected here
  @Inject('container')
  private container!: Container;

  _processing = false;

  @Watch('missionStore.deployedMission')
  onMissionChange() {
    this.renderMissionPlan();
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);

    this.addMqttVisibility(this.globalStore.machineMarkerTopic, () =>
      PubSub.subscribe(this.globalStore.machineMarkerTopic).subscribe({
        next: (data) => this.markerRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
    console.log(this.globalStore.machineRasterTopic);
    this.addMqttVisibility(this.globalStore.machineRasterTopic, () =>
      PubSub.subscribe(this.globalStore.machineRasterTopic).subscribe({
        next: (data) => this.rasterRecieved(data),
        error: (error) => this.mqttError(error)
      })
    );
  }

  async mounted() {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.actions.forEach((act, key) => {
      this.subscriptions.set(key, act());
    });

    this.getMachines();
  }

  destroyed() {
    this.removeMqttVisibility();
  }

  private renderMissionPlan() {
    this.missionPlan = this.missionStore.deployedMission;

    this.polygons = [];
    this.polyLines = [];
    this.overlays = [];
    if (this.missionPlan == null) return;

    this.polygons.push(RenderHelper.getFieldPolygonOptionsFromMission(this.missionPlan));
    this.polygons.push(RenderHelper.getWorkAreaPolygonOptionsFromMission(this.missionPlan));
    this.polygons.push(RenderHelper.getCropAreaPolygonOptionsFromMission(this.missionPlan));
    this.polyLines.push(RenderHelper.getPathPolylineOptionsFromMission(this.missionPlan));

    this.UpdateGraphicsAsync(this.missionPlan as DeployedSinglePathMission);
  }

  /**
   * Get current active mission plan and render field definition
   */
  private async getMachines() {
    if (this.flightPathService == undefined) return;
    this.markers = [];
    this.menuStore.setLoadingMachines(true);

    this.missionPlan = this.missionStore.deployedMission;
    this.renderMissionPlan();

    try {
      const machineResponse = await this.flightPathService.getAvailableMachines();
      if (machineResponse != null) {
        const json = JSON.stringify(machineResponse.data);
        const availableMachines = JSON.parse(json) as Array<MachineKey>;
        this.machineStore.setAvailableMachines(availableMachines);
        if (this.flightPathService == undefined) return;
        const machineStatusResponse = await this.flightPathService.getMachineStatus(
          availableMachines.map((map) => {
            return map.machineId;
          })
        );
        if (machineStatusResponse != null) {
          const json = JSON.stringify(machineStatusResponse.data);

          const cachedmachines = JSON.parse(json) as Array<MachineMarker>;
          cachedmachines.forEach((element) => {
            if (element.Key.machineId == this.machineStore.selectedMachine?.machineId) this.updatePosition(element);
          });

          const machines = cachedmachines
            .filter((val) => new Date(val.SentUTC).getTime() > moment().subtract(2, 'months').toDate().getTime())
            .map((val) => val.Key);
          this.machineStore.setAvailableMachines(machines);
        }
      }
    } finally {
      this.menuStore.setLoadingMachines(false);
    }
  }

  @Watch('machineStore.selectedMachine')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSelectedMachineChange(value: MachineKey): void {
    this.getMachines();
  }

  private markerRecieved(data: MqttMessage): void {
    this.updatePosition(data.value);
  }
  private updatePosition(data: MachineMarker) {
    if (data.Location == null) return;
    const selectedMachine = this.machineStore.selectedMachine;
    if (selectedMachine?.machineId != data.Key.machineId) return;

    const machineUpdateIndex = this.updateStore.machineUpdates.findIndex(
      (o) => o.machine_id.localeCompare(data.Key.machineId, undefined, { sensitivity: 'base' }) == 0
    );
    if (machineUpdateIndex >= 0) {
      const statusUpdate = this.updateStore.machineUpdates[machineUpdateIndex];
      data = UpdateMachineUpdateIcon(data, statusUpdate);
    }

    const marker: MachineMarkerOptions = {
      UpdateMessage: data,
      Machine: data.Key,
      MarkerOptions: {
        position: {
          lat: data.Location.Location.Latitude,
          lng: data.Location.Location.Longitude
        },
        zIndex: selectedMachine != null && selectedMachine.machineId == data.Key.machineId ? 99 : 1,
        title: `Machine: ${data.Key.MachineName ?? data.Key.machineId}`
      }
    };
    const machineKey = (data as MachineMarker).Key;

    const index = this.markers.findIndex((val) => val.Machine.machineId == machineKey.machineId);

    if (index >= 0) {
      this.markers.splice(index, 1, marker);
    } else {
      this.markers.push(marker);
    }
  }

  /**
   * Requests list of grids available for update (update map).
   * Downloads raster image data for that grid layer and converts it into objecturl
   * for processing and rendering via Google Map Ground Overlay
   */
  private async UpdateGraphicsAsync(mission: DeployedSinglePathMission) {
    if (this.flightPathService == undefined) return;

    console.info('Retrieving Rasters To Date');

    const response = await this.flightPathService.updateMap(mission.JobID);
    const jsonObject = response.data as Map<string, number>;

    const map: Map<string, number> = new Map<string, number>();
    for (const [key, element] of Object.entries(jsonObject)) {
      map.set(key, element);
    }

    if (map.size > 0) {
      for (const [grid, timestamp] of map) {
        const gridLocation = this.getGridLocation(grid);
        if (gridLocation != undefined) {
          for (const layer of this.getActiveLayers().Layers) {
            const query = this.overlays.filter(
              (o) => timestamp - o.raster.TimeStamp <= 0 && o.raster.Type == layer && o.raster.GridX == gridLocation.X && o.raster.GridY == gridLocation.Y
            );
            if (query.length > 0) {
              continue;
            }
            const model = await this.flightPathService.getRasterModelJobId(gridLocation, layer, mission.TargetMachine.machineId, mission.JobID, timestamp);

            this.ProcessRaster(model);
          }
        }
      }
    }
  }

  private rasterRecieved(data: any): void {
    if (this.missionPlan == null) return;
    const selectedMachine = this.machineStore.selectedMachine;
    const response = data.value as string;
    const raster = ConvertMachineRasterMessage.stringToMachineRasterMessage(JSON.stringify(response));
    if (selectedMachine?.machineId != raster.MachineKey) return;

    const stream = Uint8Array.from(atob(raster.Base64EncodedLayer), (c) => c.charCodeAt(0));
    const GridX = raster.GridX;
    const GridY = raster.GridY;
    const responseLayerType = raster.Type as LayerType;

    const rasterModel: RasterModel = {
      ImageStream: stream,
      GridX: GridX,
      GridY: GridY,
      Type: responseLayerType,
      NorthEast: raster.NorthEast,
      SouthWest: raster.SouthWest,
      MachineKey: { machineId: raster.MachineKey },
      ErrorNotes: '',
      TimeStamp: raster.TimeStamp
    };

    this.ProcessRaster(rasterModel);
  }

  private ProcessRaster(rasterModel: RasterModel) {
    if (rasterModel.ImageStream != null && rasterModel.ImageStream.length > 0) {
      const blob = new Blob([rasterModel.ImageStream], { type: 'image/png' });
      const overlay: Overlay = {
        blob: blob,
        sw: rasterModel.SouthWest,
        ne: rasterModel.NorthEast,
        raster: rasterModel,
        zIndex: 1
      };
      const index = this.overlays.findIndex(
        (val) =>
          val.raster.Type == rasterModel.Type &&
          val.raster.MachineKey.machineId == rasterModel.MachineKey.machineId &&
          val.raster.GridX == rasterModel.GridX &&
          val.raster.GridY == rasterModel.GridY
      );

      if (index >= 0) {
        this.overlays.splice(index, 1, overlay);
      } else {
        this.overlays.push(overlay);
      }
    }
  }

  getGridLocation(url: string): GridLocation | undefined {
    const regex = /([0-9-])+/g;

    const matches = url.match(regex);
    if (matches != null && matches.length >= 2) {
      const xVal = matches[0];
      const yVal = matches[1];

      return { X: Number.parseInt(xVal), Y: Number.parseInt(yVal) };
    }
  }

  /**
   * List of Active Data Layers to Render
   */
  private getActiveLayers(): LayerList {
    return {
      ErrorNotes: '',
      Layers: [LayerType.EstimatedCoverage, LayerType.DrivenPath]
    };
  }

  get mapConfig(): GoogleMapConfig {
    return {
      locationControl: false,
      loaderOptions: { libraries: ['geometry'] },
      mapOptions: {
        styles: [{ featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] }],
        zoom: 17,
        panControl: false,
        rotateControl: false,
        center: this.globalStore.defaultMapLocation,
        mapTypeId: 'hybrid',
        mapTypeControl: false,
        zoomControl: false,
        fullscreenControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
        clickableIcons: false,
        disableDefaultUI: true
      }
    };
  }
}
